import type { ConversationStartChoice } from "@/lib/conversationContinuation";
import { Crown } from "lucide-react";

type ConversationStartChoiceSelectorProps = {
  value: ConversationStartChoice;
  onChange: (choice: ConversationStartChoice) => void;
  onUpgrade: () => void;
  subscribed: boolean;
  variant?: "default" | "ecole" | "liceo";
};

export function ConversationStartChoiceSelector({
  value,
  onChange,
  onUpgrade,
  subscribed,
  variant = "default",
}: ConversationStartChoiceSelectorProps) {
  const selectedValue: ConversationStartChoice = subscribed ? value : "new";
  const options = [
    {
      value: "new",
      label: "New Session",
      description: "Chicky still remembers your progress.",
    },
    {
      value: "continue_last",
      label: "Resume last session",
      description: null,
    },
  ] as const;

  return (
    <fieldset
      aria-label="Session start"
      onClick={(event) => event.stopPropagation()}
      className="grid w-full grid-cols-1 overflow-hidden rounded-lg border border-white/10 bg-black/25 sm:grid-cols-2"
    >
      <legend className="sr-only">Session start</legend>
      {options.map((option, index) => {
        const isProLocked = option.value === "continue_last" && !subscribed;
        const isSelected = selectedValue === option.value;

        return (
          <label
            key={option.value}
            aria-disabled={isProLocked}
            className={`relative min-w-0 px-3 py-2.5 text-left transition-colors focus-within:z-10 focus-within:ring-2 focus-within:ring-inset focus-within:ring-orange-300/60 ${
              index === 0 ? "border-b border-white/10 sm:border-r sm:border-b-0" : ""
            } ${
              isProLocked
                ? "cursor-pointer bg-black/15 hover:bg-orange-500/[0.06]"
                : isSelected
                  ? variant !== "default"
                    ? "cursor-pointer bg-school-blue/20 ring-1 ring-inset ring-school-accent/55"
                    : "cursor-pointer bg-orange-500/12 ring-1 ring-inset ring-orange-300/45"
                  : "cursor-pointer hover:bg-white/[0.04] active:bg-white/[0.07]"
            }`}
          >
            <span className="flex min-w-0 items-start gap-2">
              <input
                type="radio"
                name="listen-conversation-start-choice"
                value={option.value}
                aria-label={option.label}
                checked={isSelected}
                aria-disabled={isProLocked}
                onChange={() => {
                  if (isProLocked) {
                    onUpgrade();
                  } else {
                    onChange(option.value);
                  }
                }}
                className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer accent-orange-500"
              />
              <span className="min-w-0 flex-1">
                <span className="flex min-w-0 items-start gap-1.5">
                  <span
                    className={`min-w-0 text-xs font-bold leading-snug ${
                      isProLocked ? "text-zinc-400" : "text-white"
                    }`}
                  >
                    {option.label}
                  </span>
                  {isProLocked ? (
                    <span className="inline-flex shrink-0 items-center gap-1 rounded-md border border-orange-400/30 bg-orange-500/10 px-1.5 py-0.5 text-[9px] font-extrabold text-orange-200">
                      <Crown className="h-2.5 w-2.5" aria-hidden="true" /> PRO
                    </span>
                  ) : null}
                </span>
                {option.description ? (
                  <span className="mt-1 block text-[11px] leading-snug text-zinc-500">
                    {option.description}
                  </span>
                ) : null}
              </span>
            </span>
          </label>
        );
      })}
    </fieldset>
  );
}
